<script setup lang="ts">
import * as Sentry from "@sentry/vue"
import { useUrlSearchParams, useWindowScroll } from "@vueuse/core"
import { computed, onUnmounted, watch, watchEffect } from "vue"
import { useRouter } from "vue-router"
import { useGetEventVault, useValidateGuestAccessToken } from "@/api"
import { Footer, GuestEventFloatingActions, Navbar, SplashOverlay } from "@/components"
import { Head } from "@/components/ui"
import { useGuestRedirects } from "@/composables/useGuestRedirects"
import { ForbiddenException } from "@/http"
import { EventVaultProvider } from "@/providers"
import { useAuthStore } from "@/stores/authStore"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { useTemplateStore } from "@/stores/templateStore"
import { UserRole } from "@/types"

const props = defineProps<{
  hideFooter?: boolean
  hideNavbarItems?: boolean
  isNavbarMinimal?: boolean
  isFooterBottomExtended?: boolean
  pageToken?: string
  hideFloatingActions?: boolean
}>()

const urlParams = useUrlSearchParams<{ album?: unknown }>()

const albumId = computed(() => {
  return urlParams.album && typeof urlParams.album === "string" ? Number(urlParams.album) : undefined
})

const eventVaultStore = useEventVaultStore()
const templateStore = useTemplateStore()
const authStore = useAuthStore()
const router = useRouter()

const { data: eventVault, isLoading, isRefetching } = useGetEventVault(eventVaultStore.eventVaultId)

const { isLoading: isGuestAccessTokenLoading, isError: isGuestAccessTokenError, error: guestAccessTokenError } = useValidateGuestAccessToken(props.pageToken, eventVaultStore.eventVaultId, albumId)

const { handleGuestRouting } = useGuestRedirects(eventVault, albumId, props.pageToken)

watchEffect(() => {
  if (isLoading.value || isRefetching.value || isGuestAccessTokenLoading.value)
    return

  if (isGuestAccessTokenError.value && guestAccessTokenError.value instanceof ForbiddenException) {
    Sentry.withScope((scope) => {
      scope.setLevel("error")

      scope.setTags({
        area: "guest_token",
        component: "GuestLayout",
      })

      scope.captureException(guestAccessTokenError.value)
    })

    if (props.pageToken) {
      return router.push(`/guest/${eventVaultStore.eventVaultId}/login`)
    }

    return router.push("/403")
  }

  eventVaultStore.setIsHost(eventVault.value?.isHost ?? false)

  handleGuestRouting()
})

watchEffect(() => {
  const eventVaultId = eventVaultStore.eventVaultId
  const eventTemplate = eventVault.value?.eventTemplate
  const templateCss = eventTemplate?.templateCss
  const themeJson = eventTemplate?.themeJson

  if (!eventVaultId || (!templateCss && !themeJson)) {
    templateStore.clearTemplate()
    return
  }

  templateStore.setTemplate(eventTemplate)
  templateStore.applyTemplateStyles()
})

onUnmounted(() => {
  templateStore.clearTemplate()
  eventVaultStore.setIsHost(false)
})

watch(() => eventVaultStore.eventVaultId, () => {
  templateStore.clearTemplate()
})

const SCROLL_THRESHOLD = 500
const { y: verticalScrollOffset } = useWindowScroll()
const showScrollToTop = computed(() => verticalScrollOffset.value > SCROLL_THRESHOLD)
</script>

<template>
  <Head>
    <link
      v-if="templateStore.baseFontValue"
      :href="`https://fonts.googleapis.com/css2?family=${templateStore.baseFontValue}&display=swap`" rel="stylesheet"
    >
    <link
      v-if="templateStore.headingFontValue"
      :href="`https://fonts.googleapis.com/css2?family=${templateStore.headingFontValue}&display=swap`"
      rel="stylesheet"
    >
  </Head>

  <SplashOverlay v-if="isLoading || isGuestAccessTokenLoading" />

  <div v-show="!isLoading && !isGuestAccessTokenLoading" class="guest-layout min-h-screen flex flex-col">
    <EventVaultProvider :event-vault-id="eventVaultStore.eventVaultId">
      <header>
        <slot name="navbar">
          <Navbar
            :image-src="templateStore.customHeaderImage"
            :hide-nav-items="hideNavbarItems" :is-host="eventVaultStore.isHost"
            :is-admin="authStore.user?.roles.includes(UserRole.Admin)"
            :is-minimal="isNavbarMinimal"
          />
        </slot>
      </header>
      <main class="grow">
        <div class="grow">
          <slot name="default" />
        </div>
      </main>
      <footer v-if="!hideFooter">
        <slot name="footer">
          <Footer
            :custom-logo="templateStore.customFooterImage"
            :custom-footer-links="templateStore.customFooterLinks"
            :is-freemium-event="eventVault?.freemiumMode"
            :extend-bottom-padding="isFooterBottomExtended"
          />
        </slot>
      </footer>
      <GuestEventFloatingActions v-if="!props.hideFloatingActions" :visible="showScrollToTop" />
    </EventVaultProvider>
  </div>
</template>

<style>
.guest-layout h1,
.guest-layout h2,
.guest-layout h3,
.guest-layout h4,
.guest-layout h5,
.guest-layout h6 {
  font-family: var(--template-heading-font), Comfortaa, "Open Sans", sans-serif;
  margin: 0;
  padding: 0;
}

.guest-layout * {
  font-family: var(--template-base-font), Montserrat, "Open Sans", sans-serif;
}
</style>
